import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useFavorites } from "@/hooks/useFavorites";

const navItems = [
  { label: "首页", to: "/" },
  { label: "电影", to: "/category/movie" },
  { label: "电视剧", to: "/category/tv" },
  { label: "综艺", to: "/category/variety" },
  { label: "关于我们", to: "/about" },
];

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const { favorites } = useFavorites();

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const handleSearch = (event: FormEvent) => {
    event.preventDefault();
    const q = keyword.trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
    setKeyword("");
    setMenuOpen(false);
  };

  const isActive = (to: string) => {
    if (to === "/") return location.pathname === "/";
    return location.pathname.startsWith(to);
  };

  return (
    <header className="sticky top-0 z-50 bg-primary-950 text-background-50">
      <div className="w-full px-4 md:px-6">
        <div className="mx-auto flex h-16 max-w-[1400px] items-center gap-4">
          <Link to="/" className="flex shrink-0 items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-accent-500 font-heading text-lg font-black text-background-50">
              91
            </span>
            <span className="font-heading text-lg font-bold tracking-wide text-background-50">
              看片
            </span>
          </Link>

          <nav className="ml-4 hidden items-center gap-1 md:flex">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`cursor-pointer whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  isActive(item.to)
                    ? "bg-primary-800 text-accent-300"
                    : "text-background-100/85 hover:bg-primary-800/70 hover:text-background-50"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <form
            onSubmit={handleSearch}
            className="ml-auto hidden items-center md:flex"
            role="search"
          >
            <div className="flex h-9 items-center gap-2 rounded-md bg-primary-900 px-3 focus-within:bg-primary-800">
              <i className="ri-search-line text-sm text-background-100/70" />
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索片名或主演"
                className="w-40 bg-transparent text-sm text-background-50 placeholder:text-background-100/50 focus:outline-none lg:w-56"
                aria-label="搜索影视"
              />
            </div>
            <button
              type="submit"
              className="ml-2 h-9 cursor-pointer whitespace-nowrap rounded-md bg-accent-500 px-4 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
            >
              搜索
            </button>
          </form>

          <Link
            to="/favorites"
            className="relative ml-auto hidden h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-md text-background-100 transition-colors hover:bg-primary-800 md:ml-2 md:flex"
            aria-label="我的收藏"
          >
            <i className="ri-heart-3-line text-lg" />
            {favorites.length > 0 && (
              <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent-500 px-1 text-[10px] font-bold text-background-50">
                {favorites.length}
              </span>
            )}
          </Link>

          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            className="ml-auto flex h-9 w-9 cursor-pointer items-center justify-center rounded-md text-background-50 transition-colors hover:bg-primary-800 md:hidden"
            aria-label="打开菜单"
          >
            <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-xl`} />
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="border-t border-primary-800 bg-primary-950 md:hidden">
          <div className="px-4 pb-4 pt-3">
            <form onSubmit={handleSearch} className="mb-3 flex gap-2" role="search">
              <div className="flex h-10 flex-1 items-center gap-2 rounded-md bg-primary-900 px-3">
                <i className="ri-search-line text-sm text-background-100/70" />
                <input
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索片名或主演"
                  className="w-full bg-transparent text-sm text-background-50 placeholder:text-background-100/50 focus:outline-none"
                  aria-label="搜索影视"
                />
              </div>
              <button
                type="submit"
                className="h-10 cursor-pointer whitespace-nowrap rounded-md bg-accent-500 px-4 text-sm font-semibold text-background-50"
              >
                搜索
              </button>
            </form>
            <nav className="flex flex-col">
              {navItems.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`cursor-pointer rounded-md px-3 py-3 text-sm font-medium ${
                    isActive(item.to) ? "bg-primary-800 text-accent-300" : "text-background-100"
                  }`}
                >
                  {item.label}
                </Link>
              ))}
              <Link
                to="/favorites"
                className="flex cursor-pointer items-center gap-2 rounded-md px-3 py-3 text-sm font-medium text-background-100"
              >
                <i className="ri-heart-3-line text-base" />
                我的收藏
                {favorites.length > 0 && (
                  <span className="ml-1 rounded-full bg-accent-500 px-2 text-[11px] font-bold text-background-50">
                    {favorites.length}
                  </span>
                )}
              </Link>
            </nav>
          </div>
        </div>
      )}
    </header>
  );
}